import { AutoDimensionType } from "building-types/autoDimensions/autoDimensionTypesEnum";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { isWallFaceRule } from "building-types/walls2/wallPlaneRules";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { getContentTemplateSubType } from "../contentTemplateTools";
import { docGetCatalogItem } from "../docGetCatalogItem";
import { docGetContentTemplate } from "../docGetContentTemplates";
import { electricalDeviceTypeToPrettyName } from "../utils/electricalDeviceTools";
import { openingTypeToPrettyName } from "../utils/openingTools";
import { docGetTableEntity } from "./docGetTableEntity";

export function docEntityTypePrettyName(doc: Doc, handle: DocEntityHandle): string {
  if (!handle.type) {
    return "";
  }

  if (handle.type === "SETTINGS") {
    const settings = docGetTableEntity(doc, handle);

    if (settings) {
      return snakeCaseToTitleCase(settings.type);
    }

    return "";
  }

  return docEntityTypePrettyNameNotSettings(doc, handle);
}

export function docEntityTypePrettyNameNotSettings(doc: Doc, handle: DocEntityHandle): string {
  if (!handle.type) {
    return "";
  }

  switch (handle.type) {
    case "PLAN_SET_TEMPLATE_CONFIG":
      return "Plan Set Instance";
    case "FACADE_SPAN_STRATUM":
      return "Facade Finish";
    case "FACADE_SWEEP_STRATUM":
      return "Facade Trim";
    case "OPTION_SET":
      return "Option Set";
    case "DOC_REF":
      return "Document Reference";
    case "AUTO_DIMENSION": {
      const provenanceType = handle?.provenance?.type;
      if (!provenanceType) {
        return "";
      }

      switch (provenanceType) {
        case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE: {
          return "Exterior By Room Edge";
        }
        case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ORIENTATION: {
          return "Exterior By Orientation";
        }
        case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_SINGLE: {
          return "Exterior By Room Edge Single";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE: {
          return "Exterior Foundation By Room Edge";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ORIENTATION: {
          return "Exterior Foundation By Orientation";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_SINGLE: {
          return "Exterior Foundation By Room Edge Single";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_MULTIPLE: {
          return "Interior Foundation By Room Edge";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ORIENTATION: {
          return "Interior Foundation By Orientation";
        }
        case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_INTERIOR_BY_ROOM_SINGLE: {
          return "Interior Foundation By Room Edge Single";
        }
        case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR: {
          return "Interior By Opposite Room Edge Pair";
        }
        case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE: {
          return "Interior By Orthogonal Room Edge";
        }
      }
    }
    case "REFERENCE_GEOM":
      return "Reference Geometry";
    case "VIEW_3D": // We can't name the type "3DView" due to canonical limitations with lexers
      return "3D View";
    case "ELECTRICAL_DEVICE": {
      const electricalDevice = docGetTableEntity(doc, handle);
      if (!electricalDevice) {
        return "";
      }
      return electricalDeviceTypeToPrettyName[electricalDevice.deviceType];
    }
    case "OPENING": {
      const opening = docGetTableEntity(doc, handle);
      if (!opening) {
        return "";
      }
      return openingTypeToPrettyName[opening.type];
    }
    case "FLOOR_PERIMETER_OPENING":
      return "Floor Opening";
    case "CONTENT_TEMPLATE_INSTANCE": {
      const instance = docGetTableEntity(doc, handle);
      if (!instance) {
        return "";
      }
      const template = docGetContentTemplate(doc, { type: "CONTENT_TEMPLATE", id: instance.contentTemplateId });
      return `${getContentTemplateSubType(template)} Instance`;
    }
    case "CONTENT_TEMPLATE": {
      const template = docGetContentTemplate(doc, handle);
      return `${getContentTemplateSubType(template)} Template`;
    }
    case "CATALOG_ITEM": {
      const catalogItem = docGetCatalogItem(doc, handle);
      if (!catalogItem) {
        return "";
      }
      return snakeCaseToTitleCase(catalogItem.catalogItemType);
    }
    case "WALL_PLANE_RULE": {
      const rule = docGetTableEntity(doc, handle);
      if (!rule) {
        return "Wall Plane Rule";
      }
      return isWallFaceRule(rule) ? "Wall Face Rule" : "Wall Plane Rule";
    }
    case "WALL_PLANE_TRIM":
      return "Wall Plane Trim";
    case "EDGE":
      return `${snakeCaseToTitleCase(handle.parent.type)} Edge`;
    default:
      return snakeCaseToTitleCase(handle.type);
  }
}
